import { useRef, useEffect, useCallback } from "react"
import { useRecoilValue } from "recoil"
import { HIGH_VIDEO_STREAM_TYPE, LOW_VIDEO_STREAM_TYPE } from "../../constants"
import {
  MainVideoStreamTypeAtom,
  UplinkStatusAtom
} from "../../recoil/atoms/remoteVideoStreamType"
import { userActionsSelector } from "../../recoil/atoms/users"
import { ConsoleLogger } from "../../util/logger"
import { ToastConfigProps, useCustomToast } from "../useToast"

const logger = new ConsoleLogger()
const { UplinkStatusLowCompute, UplinkStatusPoor } =
  window.MeetOnline.RTCUplinkStatus

const useNotifyUserAboutStreamQuality = () => {
  const mainVideoStreamType = useRecoilValue(MainVideoStreamTypeAtom)
  const uplinkStatus = useRecoilValue(UplinkStatusAtom)
  const runFirstTime = useRef(true)
  const config: ToastConfigProps = {
    toastOptions: {
      containerStyle: {
        margin: "0.1rem"
      }
    },
    titleStyles: {
      fontWeight: 700
    }
  }
  const showToastMessage = useCustomToast(config)
  const { getUserCount } = useRecoilValue(userActionsSelector)

  useEffect(() => {
    if (runFirstTime.current) {
      // Don't show toast for first time because default is High stream
      runFirstTime.current = false
      return
    }
    if (mainVideoStreamType === HIGH_VIDEO_STREAM_TYPE) {
      logger.log(
        "Toast - We have switched you to a higher quality stream as your downlink speed has improved"
      )
      showToastMessage(
        undefined,
        "We have switched you to a higher quality stream as your downlink speed has improved"
      )
    } else if (mainVideoStreamType === LOW_VIDEO_STREAM_TYPE) {
      logger.log("Toast - Poor downlink speed detected")
      showToastMessage(
        "Poor downlink speed detected",
        "You have been switched to a lower quality stream. If you improve your conditions by moving closer to your router or not sharing your connection with others, you will be switched back to a high quality stream.",
        { type: "WARNING" } 
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainVideoStreamType])

  const showUplinkIssueToastMessage = useCallback(async uplinkStatus => {
    if (uplinkStatus === UplinkStatusPoor) {
      logger.log("Toast: Poor uplink speed detected")
      showToastMessage(
        "Poor uplink speed detected",
        "If possible, please move closer to your wifi router and ask others using your internet connection to wait.",
        { type: "WARNING" }
      )
    } else if (uplinkStatus === UplinkStatusLowCompute) {
      const userCount = await getUserCount()
      if (userCount > 2) {
        logger.log("Toast: Poor uplink quality detected")
        showToastMessage(
          "Poor uplink quality detected",
          "Your system is running low on resources. If possible, please close other applications to free up memory.",
          { type: "WARNING" }
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    showUplinkIssueToastMessage(uplinkStatus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uplinkStatus])
}

export default useNotifyUserAboutStreamQuality
